"use client";

import * as React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { logger } from "@/lib/default-logger";
import { setAccessToken, setAccessTokenPromise } from "@/components/auth/tokenManager";
import { useEffect, useState } from "react";

export interface AuthGuardProps {
    children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps): React.JSX.Element | null {
    const {
        isAuthenticated,
        isLoading,
        loginWithRedirect,
        getAccessTokenSilently,
    } = useAuth0();

    const [init, setInit] = useState<boolean>(false);

    useEffect(() => {
        let cancelled = false;

        async function initializeAuth(): Promise<void> {
            if (isLoading) {
                return;
            }

            if (!isAuthenticated) {
                logger.debug("[AuthGuard] User is not authenticated, redirecting to sign in");
                await loginWithRedirect();
                return;
            }

            try {
                const tokenPromise = getAccessTokenSilently()
                    .then(token => token ?? null);

                setAccessTokenPromise(tokenPromise);

                const token = await tokenPromise;

                if (cancelled) {
                    return;
                }

                if (!token) {
                    setInit(false);
                    return;
                }

                setAccessToken(token);
                setInit(true);
            } catch (error) {
                if (cancelled) {
                    return;
                }

                logger.error("[AuthGuard] Failed to get Auth0 access token", error);
                setInit(false);
                await loginWithRedirect();
            }
        }

        initializeAuth();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently]);

    if (isLoading || !isAuthenticated || !init) {
        return null;
    }

    return <React.Fragment>{children}</React.Fragment>;
}
